import { useState } from 'react';
import { ScanText } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../ui/dialog';
import ScreenshotReader from '../ocr/ScreenshotReader';

const API = process.env.REACT_APP_BACKEND_URL;

/**
 * Phase 3 — user copy-pastes the real barcodes, submits prices per company.
 *
 * For MVP simplicity we present a grid: each row = barcode, each column = company price input.
 * User pastes bulk values; blank = skipped (no data point credited).
 */
export default function BatchSubmission({ batch, onSubmitted, onCancel }) {
  const [copied, setCopied] = useState(false);
  const [prices, setPrices] = useState({}); // { barcode: { musicmagpie: '', webuybooks: '', ... } }
  const [err, setErr] = useState('');
  const [loading, setLoading] = useState(false);
  const [ocrCompany, setOcrCompany] = useState(null);

  const companies = batch.companies || [];
  const realBarcodes = batch.real_barcodes || [];
  const usesCex = companies.includes('cex');

  const columns = [
    ...companies.filter((c) => c !== 'cex').map((c) => ({ key: c, label: COMPANY_LABELS[c] })),
    ...(usesCex
      ? [
          { key: 'cex_cash', label: 'CEX Cash' },
          { key: 'cex_voucher', label: 'CEX Voucher' },
        ]
      : []),
  ];

  const copyAll = () => {
    navigator.clipboard.writeText(realBarcodes.join('\n'));
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  };

  const setCell = (barcode, key, value) => {
    setPrices((prev) => ({
      ...prev,
      [barcode]: { ...(prev[barcode] || {}), [key]: value },
    }));
  };

  const applyOcr = (company, rows) => {
    setPrices((prev) => {
      const next = { ...prev };
      rows.forEach((r) => { next[r.match_barcode] = { ...(next[r.match_barcode] || {}), [company]: String(r.price) }; });
      return next;
    });
    setOcrCompany(null);
  };

  // Points entered so far
  const pointsEntered = Object.values(prices).reduce((sum, row) => {
    return (
      sum +
      Object.values(row || {}).filter((v) => v !== '' && v !== null && !isNaN(parseFloat(v))).length
    );
  }, 0);

  const submit = async () => {
    setErr('');
    setLoading(true);
    try {
      const cleaned = {};
      for (const [bc, row] of Object.entries(prices)) {
        const kept = {};
        for (const [k, v] of Object.entries(row || {})) {
          if (v === '' || v === null) continue;
          const n = parseFloat(v);
          if (!isNaN(n)) kept[k] = n;
        }
        if (Object.keys(kept).length > 0) cleaned[bc] = kept;
      }

      const res = await fetch(`${API}/api/batches/${batch.batch_id}/submit`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ prices: cleaned }),
      });
      const data = await res.json();
      if (!res.ok) setErr(data.detail || 'Could not submit.');
      else onSubmitted(data);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="batch-submission">
      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 mb-4">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h3 className="text-base font-bold text-white">Real barcodes ({realBarcodes.length})</h3>
            <p className="text-xs text-zinc-500">Paste into each buyer site; enter the prices below.</p>
          </div>
          <button
            onClick={copyAll}
            className="px-3 py-2 rounded-full text-xs font-bold border border-zinc-700 hover:bg-zinc-800"
            data-testid="copy-real-barcodes-btn"
          >
            {copied ? '✓ Copied' : `📋 Copy all ${realBarcodes.length}`}
          </button>
        </div>
        <div className="bg-black rounded-lg p-3 max-h-40 overflow-auto text-[11px] font-mono text-zinc-400">
          {realBarcodes.slice(0, 12).map((b) => (
            <div key={b}>{b}</div>
          ))}
          {realBarcodes.length > 12 && (
            <div className="text-zinc-500 italic mt-1">…and {realBarcodes.length - 12} more (copy button gets them all)</div>
          )}
        </div>
      </div>

      {companies.some((c) => c !== 'cex') && (
        <div className="flex flex-wrap gap-2 mb-3" data-testid="ocr-fill-buttons">
          {companies.filter((c) => c !== 'cex').map((c) => (
            <button key={c} onClick={() => setOcrCompany(c)} className="flex items-center gap-1.5 px-3 py-2 rounded-full text-xs font-bold border border-zinc-700 hover:border-[#00D26A] hover:text-[#00D26A] transition-colors" data-testid={`ocr-fill-${c}-btn`}>
              <ScanText className="w-3.5 h-3.5" /> Fill {COMPANY_LABELS[c]} from screenshot
            </button>
          ))}
        </div>
      )}
      <Dialog open={!!ocrCompany} onOpenChange={(o) => !o && setOcrCompany(null)}>
        <DialogContent className="max-w-3xl bg-zinc-950 border-zinc-800 text-zinc-200 max-h-[90vh] overflow-y-auto" data-testid="ocr-dialog">
          <DialogHeader>
            <DialogTitle className="text-white">{COMPANY_LABELS[ocrCompany]} basket screenshots</DialogTitle>
          </DialogHeader>
          {ocrCompany && <ScreenshotReader fixedCompany={ocrCompany} batchId={batch.batch_id} onConfirm={applyOcr} confirmLabel="Fill grid" />}
        </DialogContent>
      </Dialog>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4 overflow-x-auto">
        <table className="w-full text-sm" data-testid="batch-price-grid">
          <thead>
            <tr className="text-xs text-zinc-400 text-left">
              <th className="py-2 pr-3">Barcode</th>
              {columns.map((c) => (
                <th key={c.key} className="py-2 pr-3 text-center">
                  {c.label}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {realBarcodes.map((bc, idx) => (
              <tr key={bc} className="border-t border-zinc-800">
                <td className="py-2 pr-3 font-mono text-[11px] text-zinc-400">
                  <span className="text-zinc-600 mr-2">{idx + 1}</span>
                  {bc}
                </td>
                {columns.map((c) => (
                  <td key={c.key} className="py-1 pr-3">
                    <input
                      type="number"
                      step="0.01"
                      min="0"
                      placeholder="—"
                      value={prices[bc]?.[c.key] ?? ''}
                      onChange={(e) => setCell(bc, c.key, e.target.value)}
                      className="w-20 bg-black border border-zinc-800 rounded px-2 py-1 text-right font-mono text-xs focus:outline-none focus:border-[#00D26A]"
                      data-testid={`price-${c.key}-${idx}`}
                    />
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="bg-zinc-900 border border-zinc-800 rounded-xl p-4 mb-4 flex items-center justify-between text-sm">
        <div>
          <span className="text-zinc-400">Points entered so far: </span>
          <strong className="text-white">{pointsEntered}</strong>
          <span className="text-zinc-500"> / {batch.max_possible_earnings * 100}</span>
        </div>
        <div>
          <span className="text-zinc-400">Projected earnings: </span>
          <strong className="text-[#00D26A]">£{(pointsEntered * 0.01).toFixed(2)}</strong>
        </div>
      </div>

      {err && (
        <p className="text-sm mb-3" style={{ color: '#E04A3F' }} data-testid="submit-error">
          {err}
        </p>
      )}

      <div className="flex gap-2 justify-end">
        <button
          onClick={onCancel}
          className="px-4 py-2 rounded-full font-bold text-sm border border-zinc-700 text-zinc-300 hover:bg-zinc-800"
          data-testid="submit-cancel-btn"
        >
          Cancel
        </button>
        <button
          onClick={submit}
          disabled={loading || pointsEntered === 0}
          className="px-6 py-2 rounded-full font-bold text-sm disabled:opacity-50"
          style={{ background: 'linear-gradient(90deg,#00D26A,#FFD700)', color: '#050505' }}
          data-testid="submit-prices-btn"
        >
          {loading ? 'Submitting…' : `Submit & earn £${(pointsEntered * 0.01).toFixed(2)}`}
        </button>
      </div>
    </div>
  );
}

const COMPANY_LABELS = {
  musicmagpie: 'MusicMagpie',
  webuybooks: 'WeBuyBooks',
  worldofbooks: 'World of Books',
};
